// =============================================================
// Theme (light/dark) + language (LV/RU/EN) preferences — from @perpetuum/core.
//
// Theme drives the CSS-variable bridge (lib/theme-css). Language drives the
// core `t()` translator; the binding constraint (PROJECT.md) is that every
// screen supports LV/RU/EN with LIVE switching, so this is a top-level context.
// =============================================================

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LANGS, makeT, type Lang, type ThemeName, type TVars } from "@perpetuum/core";
import { applyTheme } from "../lib/theme-css.js";

/** Where the primary navigation lives. */
export type NavMode = "side" | "top";

interface Prefs {
  theme: ThemeName;
  lang: Lang;
  setTheme: (t: ThemeName) => void;
  toggleTheme: () => void;
  setLang: (l: Lang) => void;
  langs: readonly Lang[];
  /** Navigation layout: side rail (default) or top bar. */
  navMode: NavMode;
  setNavMode: (m: NavMode) => void;
  /** Manual collapse of the side rail (persisted). */
  navCollapsed: boolean;
  setNavCollapsed: (c: boolean) => void;
  /** Translator bound to the active language (live). */
  t: (key: string, vars?: TVars) => string;
}

const PrefsCtx = createContext<Prefs | null>(null);

const THEME_KEY = "perp.theme";
const LANG_KEY = "perp.lang";
const NAV_MODE_KEY = "perp.nav-mode";
const NAV_COLLAPSED_KEY = "perp.nav-collapsed";
const NAV_MODES: readonly NavMode[] = ["side", "top"];

function readStored<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => readStored<ThemeName>(THEME_KEY, "dark", ["light", "dark"]));
  const [lang, setLangState] = useState<Lang>(() => readStored<Lang>(LANG_KEY, "en", LANGS));
  const [navMode, setNavModeState] = useState<NavMode>(() => readStored<NavMode>(NAV_MODE_KEY, "side", NAV_MODES));
  const [navCollapsed, setNavCollapsedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Apply the theme CSS vars on mount + whenever it changes (live switch).
  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
      document.documentElement.lang = lang;
    } catch {
      /* ignore */
    }
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_MODE_KEY, navMode);
    } catch {
      /* ignore */
    }
  }, [navMode]);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_COLLAPSED_KEY, navCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [navCollapsed]);

  const setTheme = useCallback((t: ThemeName) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);
  const setLang = useCallback((l: Lang) => setLangState(l), []);
  const setNavMode = useCallback((m: NavMode) => setNavModeState(m), []);
  const setNavCollapsed = useCallback((cc: boolean) => setNavCollapsedState(cc), []);

  const value = useMemo<Prefs>(
    () => ({
      theme,
      lang,
      setTheme,
      toggleTheme,
      setLang,
      langs: LANGS,
      navMode,
      setNavMode,
      navCollapsed,
      setNavCollapsed,
      t: makeT(lang),
    }),
    [theme, lang, setTheme, toggleTheme, setLang, navMode, setNavMode, navCollapsed, setNavCollapsed],
  );

  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}

export function usePrefs(): Prefs {
  const ctx = useContext(PrefsCtx);
  if (!ctx) throw new Error("usePrefs must be used within PrefsProvider");
  return ctx;
}
